import { Injectable, computed, inject } from '@angular/core';
import { I18nService } from '../core/i18n.service';
import { googleAdsCopy } from './google-ads.i18n';

type Copy = { readonly tr: string; readonly en: string };
const copyByText = new Map<string, Copy>();
for (const [key, copy] of Object.entries(googleAdsCopy)) {
  copyByText.set(key, copy);
  copyByText.set(copy.tr, copy);
  // Preserve the first entry when two Turkish labels share an English translation.
  if (!copyByText.has(copy.en)) copyByText.set(copy.en, copy);
}

@Injectable({ providedIn: 'root' })
export class GoogleAdsI18nService {
  private readonly i18n = inject(I18nService);
  readonly language = computed(() => this.i18n.lang() === 'tr' ? 'tr' : 'en');
  readonly locale = computed(() => this.language() === 'tr' ? 'tr-TR' : 'en-US');

  t(value: unknown): string {
    const text = String(value ?? '');
    const copy = copyByText.get(text) ?? copyByText.get(text.trim());
    if (copy) return copy[this.language()];
    if (this.language() === 'en') {
      let match: RegExpMatchArray | null;
      if ((match = text.match(/^(\d+) hesap güncellendi\.$/))) return this.accountsUpdated(Number(match[1]));
      if ((match = text.match(/^(.+) 1–(\d+) karakter olmalı\.$/))) return `${this.t(match[1])} must be 1–${match[2]} characters long.`;
      if ((match = text.match(/^(.+) sayısı (\d+)–(\d+) olmalı\.$/))) return `${this.t(match[1])}: use ${match[2]}–${match[3]} items.`;
      if ((match = text.match(/^Google bu kampanya için en az (.+) günlük bütçe istiyor\.$/))) return `Google requires a daily budget of at least ${match[1]} for this campaign.`;
      if ((match = text.match(/^Bağlantı #(\d+):\s*(.+)$/))) return `Connection #${match[1]}: ${this.t(match[2])}`;
      for (const prefix of ['Faturalama verisi erişilebilir değil: ', 'Google video aktarımı başlatılamadı. (HTTP ', 'Video aktarımı tamamlanamadı. (HTTP ']) {
        if (text.startsWith(prefix)) return this.t(prefix) + text.slice(prefix.length);
      }
      if ((match = text.match(/^(.+) \(HTTP (\d+)\)$/))) return `${this.t(match[1])} (HTTP ${match[2]})`;
      if (text.includes(' | ') || text.includes(' · ')) return text.split(/( \| | · )/).map(part => part === ' | ' || part === ' · ' ? part : this.t(part)).join('');
    }
    // Allow active UI messages to follow a subsequent switch back to Turkish.
    const updated = text.match(/^(\d+) accounts? updated\.$/);
    if (updated) return this.accountsUpdated(Number(updated[1]));
    return text;
  }
  accountsUpdated(count: number): string {
    return this.language() === 'tr' ? `${count} hesap güncellendi.` : `${count} account${count === 1 ? '' : 's'} updated.`;
  }

  assetAria(kind: 'headline' | 'description', index: number, action: 'remove' | 'position'): string {
    if (this.language() === 'en') return action === 'remove' ? `Remove ${kind} ${index}` : `Headline ${index} position`;
    const name = kind === 'headline' ? 'Başlık' : 'Açıklama';
    return `${name} ${index} ${action === 'remove' ? 'kaldır' : 'konumu'}`;
  }

  enumLabel(value: unknown): string {
    const raw = String(value ?? '');
    const labels: Record<string, readonly [string, string]> = {
      ENABLED:['Etkin','Enabled'], PAUSED:['Duraklatılmış','Paused'], REMOVED:['Kaldırılmış','Removed'],
      UNKNOWN:['Bilinmiyor','Unknown'], UNSPECIFIED:['Belirtilmemiş','Unspecified'], PENDING:['Bekliyor','Pending'],
      APPROVED:['Uygun','Approved'], DISAPPROVED:['Reddedildi','Disapproved'], UNDER_REVIEW:['İncelemede','Under review'],
      ELIGIBLE:['Uygun','Eligible'], LIMITED:['Sınırlı','Limited'], NOT_ELIGIBLE:['Uygun değil','Not eligible'],
      SEARCH:['Arama','Search'], DISPLAY:['Görüntülü','Display'], SHOPPING:['Alışveriş','Shopping'],
      PERFORMANCE_MAX:['Performance Max','Performance Max'], DEMAND_GEN:['Demand Gen','Demand Gen'],
      MULTI_CHANNEL:['Çok kanallı','Multichannel'], VIDEO:['Video','Video'], SMART:['Smart','Smart'], LOCAL:['Yerel','Local'],
      MANUAL_CPC:['Manuel TBM','Manual CPC'], MAXIMIZE_CLICKS:['Tıklamaları artır','Maximize clicks'],
      MAXIMIZE_CONVERSIONS:['Dönüşümleri artır','Maximize conversions'], MAXIMIZE_CONVERSION_VALUE:['Dönüşüm değerini artır','Maximize conversion value'],
      TARGET_SPEND:['Tıklamaları artır','Maximize clicks'], TARGET_CPA:['Hedef EBM','Target CPA'], TARGET_ROAS:['Hedef ROAS','Target ROAS'],
      BROAD:['Geniş eşleme','Broad match'], PHRASE:['Sıralı eşleme','Phrase match'], EXACT:['Tam eşleme','Exact match'],
      CALL:['Telefon araması','Call'], SITELINK:['Site bağlantısı','Sitelink'], CALLOUT:['Vurgu metni','Callout'],
      HEADLINE:['Başlık','Headline'], LONG_HEADLINE:['Uzun başlık','Long headline'], DESCRIPTION:['Açıklama','Description'],
      MARKETING_IMAGE:['Yatay görsel','Landscape image'], SQUARE_MARKETING_IMAGE:['Kare görsel','Square image'],
      LOGO:['Logo','Logo'], BUSINESS_NAME:['İşletme adı','Business name'], YOUTUBE_VIDEO:['YouTube videosu','YouTube video'],
      IMAGE:['Görsel','Image'], TEXT:['Metin','Text'], WEBPAGE:['Web sitesi','Website'], UPLOAD_CLICKS:['Çevrimdışı tıklama','Offline click'],
      GOOGLE_ANALYTICS_4_CUSTOM:['Google Analytics 4','Google Analytics 4'], GOOGLE_ANALYTICS_4_PURCHASE:['GA4 satın alma','GA4 purchase'],
      PURCHASE:['Satın alma','Purchase'], SUBMIT_LEAD_FORM:['Form gönderimi','Lead form submission'], SIGNUP:['Kayıt','Sign-up'],
      CONTACT:['İletişim','Contact'], PAGE_VIEW:['Sayfa görüntüleme','Page view'], DOWNLOAD:['İndirme','Download'], DEFAULT:['Diğer','Other'],
      OPEN:['Açık','Open'], CLOSED:['Kapalı','Closed'], HIDDEN:['Gizli','Hidden'], MOBILE:['Mobil','Mobile'], DESKTOP:['Bilgisayar','Desktop'],
      TABLET:['Tablet','Tablet'], CONNECTED_TV:['Bağlı TV','Connected TV'], UNDETERMINED:['Belirlenemeyen','Undetermined'],
      MALE:['Erkek','Male'], FEMALE:['Kadın','Female'], KEYWORD:['Anahtar kelime','Keyword'], AD_SCHEDULE:['Yayın saatleri','Ad schedule'],
      LOCATION:['Konum','Location'], DEVICE:['Cihaz','Device'], LANGUAGE:['Dil','Language'], USER_LIST:['Ziyaretçi listesi','Visitor list'],
      AGE_RANGE:['Yaş aralığı','Age range'], GENDER:['Cinsiyet','Gender'], RESPONSIVE_SEARCH_AD:['Duyarlı arama reklamı','Responsive search ad'],
      RESPONSIVE_DISPLAY_AD:['Duyarlı görüntülü reklam','Responsive display ad'], SEARCH_STANDARD:['Standart arama','Standard search'],
      DISPLAY_STANDARD:['Standart görüntülü','Standard display'], EXCELLENT:['Mükemmel','Excellent'], GOOD:['İyi','Good'],
      AVERAGE:['Orta','Average'], POOR:['Zayıf','Poor'], INCOMPLETE:['Eksik','Incomplete'],
      SEARCH_PARTNERS:['Arama iş ortakları','Search partners'], CONTENT:['Görüntülü Reklam Ağı','Display Network'],
      MIXED:['Karma','Mixed'], MERCHANT_CENTER:['Merchant Center','Merchant Center'], HTML:['HTML','HTML'],
    };
    if (labels[raw]) return labels[raw][this.language() === 'tr' ? 0 : 1];
    const days: Record<string, string> = {MONDAY:'Pazartesi',TUESDAY:'Salı',WEDNESDAY:'Çarşamba',THURSDAY:'Perşembe',FRIDAY:'Cuma',SATURDAY:'Cumartesi',SUNDAY:'Pazar'};
    if (days[raw]) return this.t(days[raw]);
    if (/^AGE_RANGE_/.test(raw)) return raw === 'AGE_RANGE_UNDETERMINED' ? this.t('Belirlenemeyen') : raw.replace('AGE_RANGE_', '').replace('_UP', '+').replace('_', '–');
    // Future Google enums remain readable without changing their underlying values.
    return raw.replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\S/g, letter => letter.toUpperCase());
  }
}
